import type { PageType } from '@/types/nav-types'

import type { ExperimentAssignments } from './experiment-tracking'
import { utmize } from './utmize'

// Explicit destinations only: manufacturer/editorial ads must not become
// affiliate clicks merely because they are external links.
const AFFILIATE_HOSTS = [
  'sjv.io',
  'pxf.io',
  'i351785.net',
  'awin1.com',
  '7tiv.net',
  'w2t6.net',
  'tidd.ly',
  'prf.hn',
  'aoabt4trk.com',
  'audienhearing.com',
  'go.yeshearing.com',
  'hearingtrack.com',
  'audicus.com',
  'fortell.com',
  'hear.com',
  'ziphearing.com',
  'injoyhearing.com',
]

export interface OutboundAffiliateContext {
  pathname: string
  position?: string
  pageType?: PageType
  segment?: string
  dateStamp?: string
  sessionId?: string | null
  experiments?: ExperimentAssignments
}

export const isOutboundAffiliateUrl = (href?: string): boolean => {
  if (!href || !/^https?:\/\//i.test(href)) {
    return false
  }
  try {
    const url = new URL(href)
    const recognized = AFFILIATE_HOSTS.some((host) => url.hostname === host || url.hostname.endsWith(`.${host}`))
    // Keep deliberate off-site channel links out of website attribution.
    const channel = [
      ...['utm_source', 'utm_medium', 'subId1', 'sub1']
        .map((key) => url.searchParams.get(key) ?? '')
        .filter((value) => !/^ht[23]_/.test(value)),
      /(^|\.)hearingtrack\.com$/.test(url.hostname) ? url.pathname : '',
    ].join(' ')
    const offsite = /(?:^|[\s_-])(?:yt|youtube|email|newsletter)(?:$|[\s_-])/i.test(channel)
    return recognized && !offsite
  } catch {
    return false
  }
}

export const tagOutboundAffiliateUrl = (url: string, { position, ...context }: OutboundAffiliateContext): string =>
  utmize({ ...context, url, content: position, preserveCampaignParameters: true })
